import React, { useState, useEffect } from 'react';
import { API_BASE } from './AdminPanel/utils/constants';

// Tiny `**bold**` renderer for admin-edited handbook copy. We split on the
// pattern rather than using dangerouslySetInnerHTML so that even if an admin
// pastes raw HTML into a handbook PUT body it renders as literal text.
const renderInlineBold = (text) => {
  if (text == null) return null;
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) =>
    part.startsWith('**') && part.endsWith('**') && part.length > 4
      ? <strong key={i}>{part.slice(2, -2)}</strong>
      : <React.Fragment key={i}>{part}</React.Fragment>
  );
};

const PolicySection = ({ title, items, ordered = false }) => {
  if (!Array.isArray(items) || items.length === 0) return null;
  const ListTag = ordered ? 'ol' : 'ul';
  return (
    <>
      <h4 style={{
        fontSize: 13, fontWeight: 600, marginBottom: 8,
        textTransform: 'uppercase', letterSpacing: '0.04em',
        color: 'var(--symbolica-muted)',
      }}>{title}</h4>
      <ListTag style={{ margin: '0 0 20px', paddingLeft: 18 }}>
        {items.map((it, i) => <li key={i}>{renderInlineBold(it)}</li>)}
      </ListTag>
    </>
  );
};

export default function ReferralsPage({ user }) {
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [policyOpen, setPolicyOpen] = useState(false);
  const [policy, setPolicy] = useState(null);

  useEffect(() => {
    const fetchReferrals = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/user/referrals`, {
          headers: {
            'Content-Type': 'application/json',
            'X-User-Email': user?.email || ''
          },
          credentials: 'include'
        });

        if (!res.ok) {
          throw new Error(`Failed to fetch referrals: ${res.status}`);
        }

        const data = await res.json();
        if (data.success) {
          setReferrals(data.referrals || []);
        } else {
          throw new Error(data.error || 'Failed to fetch referrals');
        }
      } catch (e) {
        console.error('Error fetching referrals:', e);
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };

    const fetchPolicy = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/handbook/referral_program`, {
          credentials: 'include',
        });
        if (!res.ok) throw new Error(`policy fetch failed (${res.status})`);
        const data = await res.json();
        setPolicy(data?.entry?.content || {});
      } catch (e) {
        console.error('Error fetching referral policy:', e);
        setPolicy({});
      }
    };

    if (user?.email) {
      fetchReferrals();
      fetchPolicy();
    }
  }, [user]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  const getBonusStatus = (bonusDate) => {
    if (!bonusDate) return { status: 'pending', text: 'Pending', color: 'var(--symbolica-muted)' };
    
    const today = new Date();
    const bonus = new Date(bonusDate);
    const diffTime = bonus - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return { status: 'overdue', text: 'Overdue', color: '#dc2626' };
    } else if (diffDays === 0) {
      return { status: 'due', text: 'Due Today', color: '#f59e0b' };
    } else if (diffDays <= 30) {
      return { status: 'upcoming', text: `${diffDays} days`, color: '#f59e0b' };
    } else {
      return { status: 'future', text: `${diffDays} days`, color: 'var(--symbolica-muted)' };
    }
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px',
        fontSize: '16px',
        color: 'var(--symbolica-muted)'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '12px' 
        }}>
          <div style={{
            width: '24px',
            height: '24px',
            border: '2px solid #e5e7eb',
            borderTop: '2px solid var(--symbolica-black)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          Loading referrals...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px',
        fontSize: '16px',
        color: '#dc2626'
      }}>
        Error loading referrals: {error}
      </div>
    );
  }

  return (
    <div className="page-wrap">
      <div style={{ marginBottom: '2rem' }}>
        <h2 className="content-title">
          My Referrals
        </h2>
        <p style={{ color: 'var(--symbolica-muted)', fontSize: '14px' }}>
          Track your successful referrals and upcoming bonus payments
        </p>
      </div>

      {/* Add New Referral Button */}
      {policy?.submit_url && (
        <div style={{ marginBottom: '2rem' }}>
          <a
            href={policy.submit_url}
            target="_blank"
            rel="noopener noreferrer"
            className="primary-button"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              textDecoration: 'none',
              padding: '10px 20px',
            }}
          >
            {policy.submit_button_label || '+ Submit New Referral'}
          </a>
          {policy.submit_button_helper && (
            <p style={{
              fontSize: '12px',
              color: 'var(--symbolica-muted)',
              marginTop: '8px',
              marginLeft: '0'
            }}>
              {policy.submit_button_helper}
            </p>
          )}
        </div>
      )}

      {/* Referrals List */}
      {referrals.length === 0 ? (
        <div style={{
          background: '#F6F4F0',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '3rem 2rem',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '1rem' }}></div>
          <h3 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--symbolica-black)' }}>
            No referrals yet
          </h3>
          <p style={{ color: 'var(--symbolica-muted)', fontSize: '14px', marginBottom: '1.5rem' }}>
            Start referring great candidates to join our team!
          </p>
          {policy?.submit_url && (
            <a
              href={policy.submit_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                background: 'var(--symbolica-black)',
                color: 'white',
                textDecoration: 'none',
                padding: '10px 16px',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              {policy.empty_state_button_label || 'Submit Your First Referral'}
            </a>
          )}
        </div>
      ) : (
        <div style={{
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          overflow: 'hidden'
        }}>
          <div style={{
            background: '#F6F4F0',
            padding: '1rem 1.5rem',
            borderBottom: '1px solid #e5e7eb'
          }}>
            <h3 style={{ fontSize: '1rem', fontWeight: '600', color: 'var(--symbolica-black)', margin: 0 }}>
              Successful Referrals ({referrals.length})
            </h3>
          </div>
          
          <div style={{ padding: '0' }}>
            {referrals.map((referral, index) => {
              const bonusStatus = getBonusStatus(referral.referral_bonus_date);
              
              return (
                <div
                  key={referral.employee_email}
                  style={{
                    padding: '1.5rem',
                    borderBottom: index < referrals.length - 1 ? '1px solid #e5e7eb' : 'none',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                    gap: '12px',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px', flexWrap: 'wrap' }}>
                      <h4 style={{ 
                        fontSize: '1rem', 
                        fontWeight: '600', 
                        color: 'var(--symbolica-black)', 
                        margin: 0 
                      }}>
                        {referral.employee_name}
                      </h4>
                      <span style={{
                        fontSize: '12px',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        background: 'var(--symbolica-warm-border)',
                        color: 'var(--symbolica-muted)'
                      }}>
                        {referral.title}
                      </span>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '12px', fontSize: '14px', color: 'var(--symbolica-muted)', flexWrap: 'wrap' }}>
                      <div>
                        <strong>Started:</strong> {formatDate(referral.start_date)}
                      </div>
                      <div>
                        <strong>Country:</strong> {referral.country}
                      </div>
                    </div>
                  </div>
                  
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ 
                      fontSize: '14px', 
                      color: 'var(--symbolica-muted)', 
                      marginBottom: '4px' 
                    }}>
                      Referral Bonus
                    </div>
                    <div style={{ 
                      fontSize: '16px', 
                      fontWeight: '600', 
                      color: bonusStatus.color,
                      marginBottom: '4px'
                    }}>
                      {formatDate(referral.referral_bonus_date)}
                    </div>
                    <div style={{ 
                      fontSize: '12px', 
                      color: bonusStatus.color,
                      fontWeight: '500'
                    }}>
                      {bonusStatus.text}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Info Box */}
      {/* Referral Bonus Policy */}
      <div style={{
        background: 'var(--symbolica-card)',
        border: '1px solid var(--symbolica-warm-border)',
        borderRadius: 'var(--radius)',
        marginTop: '2rem',
        overflow: 'hidden',
      }}>
        <button
          onClick={() => setPolicyOpen(!policyOpen)}
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '18px 24px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontFamily: 'var(--font-body)',
          }}
        >
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--symbolica-black)', marginBottom: 2 }}>
              {policy?.header_title || 'Referral Bonus'}
            </div>
            <div style={{ fontSize: 13, color: 'var(--symbolica-muted)' }}>
              {policyOpen
                ? (policy?.header_subtitle_open || 'Eligibility, rules, and how to submit')
                : (policy?.header_subtitle_closed || 'Find out more about our referral programme')}
            </div>
          </div>
          <span style={{
            fontSize: 18, color: 'var(--symbolica-muted)',
            transform: policyOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
            flexShrink: 0,
          }}>
            &#8964;
          </span>
        </button>

        {policyOpen && (
          <div style={{
            padding: '0 24px 24px',
            fontSize: 13,
            lineHeight: 1.7,
            color: 'var(--symbolica-black)',
          }}>
            <div style={{ borderTop: '1px solid var(--symbolica-warm-border)', paddingTop: 20 }}>
              {policy === null ? (
                <div style={{ color: 'var(--symbolica-muted)' }}>Loading policy&hellip;</div>
              ) : (
                <>
                  {policy.intro && (
                    <p style={{ color: 'var(--symbolica-muted)', marginBottom: 20 }}>
                      {renderInlineBold(policy.intro)}
                    </p>
                  )}
                  <PolicySection title="Eligibility" items={policy.eligibility} />
                  <PolicySection title="Rules" items={policy.rules} />
                  <PolicySection title="Bonus Details" items={policy.bonus_details} />
                  <PolicySection title="How to Submit" items={policy.how_to_submit} ordered />
                  {policy.footer && (
                    <div style={{
                      background: '#F0EDE6',
                      borderRadius: 'var(--radius-sm)',
                      padding: '12px 16px',
                      fontSize: 12,
                      color: 'var(--symbolica-muted)',
                    }}>
                      {renderInlineBold(policy.footer)}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
